'use client';

import {
  type DehydratedState,
  HydrationBoundary,
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';

import { captureError } from '@/lib/posthog/captureError';

import { InitialPriceHydration, type PendingInitialPrice } from './InitialPriceHydration';
import { makeMutationErrorLogContext } from './react_query_error_diagnostics';
import { shouldSkipError } from './utils';

// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

interface ReactQueryProviderProps {
  children: React.ReactNode;
  state?: DehydratedState;
  pendingPrice?: PendingInitialPrice;
}

const makeQueryClient = () => {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (query.meta?.reportError === false) return;
        if (shouldSkipError(error, query.queryKey)) {
          return;
        }

        captureError(error, {
          source: 'TANSTACK_QUERY_ERROR',
        });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, variables, _context, mutation) => {
        const willSkip = shouldSkipError(error, mutation.options.mutationKey);
        const logContext = makeMutationErrorLogContext({
          error,
          mutationKey: mutation.options.mutationKey,
          variables,
          willSkip,
        });

        if (willSkip) {
          return;
        }

        captureError(error, {
          source: 'TANSTACK_MUTATION_ERROR',
          additionalContext: {
            mutationKey: mutation.options.mutationKey,
            status: mutation.state.status,
            failureCount: mutation.state.failureCount,
            submittedAt: mutation.state.submittedAt,
            meta: mutation.options.meta,
            http: logContext.http,
            variables: logContext.variables,
          },
        });
      },
    }),
  });
};

let browserQueryClient: QueryClient | undefined;

export function getQueryClient() {
  if (globalThis.window === undefined) {
    // Server: always make a new query client
    return makeQueryClient();
  }
  // Browser: make a new query client if we don't already have one
  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient();
  }
  return browserQueryClient;
}

export const ReactQueryProvider = ({ children, state, pendingPrice }: ReactQueryProviderProps) => {
  const queryClient = getQueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      {/* Keep the root hydration reference in this client entry. A shared RSC
          reference to the library export can select a child page's whole bundle. */}
      <HydrationBoundary state={state}>
        <InitialPriceHydration pending={pendingPrice}>{children}</InitialPriceHydration>
      </HydrationBoundary>
      {/* {process.env.NEXT_PUBLIC_NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />} */}
    </QueryClientProvider>
  );
};
